import React from "react";
import {
    Container,
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Stack
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLightbulb,
    faExclamationTriangle,
    faCheckCircle,
    faGavel,
    faEuroSign,
    faInfoCircle,
    faFutbol, faDownload, faFilePdf
} from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "@others/title-section.layout";

const POST20250411: React.FC = () => {
    return (
        <>
            <Container sx={{ mb: 5 }}>
                <SectionTitle title={"ESPACE INFORMATIONS DES HABITANTS"} />
                <Box mt={3}>
                    <Typography variant="h6" fontWeight={700} align="center" gutterBottom color="primary">
                        Retour sur le Conseil Municipal du 10 avril 2025
                    </Typography>

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Quand le Conseil Municipal est devenu pour une représentation le dernier endroit où l’on peut jouer « Le Menteur » de Corneille...
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body1">
                        Lors du Conseil Municipal du <strong>10 avril 2025</strong>, les élus de la majorité ont scellé l’enterrement du projet d’équipements sportifs suivant une mise en scène orchestrée par le Maire. Personne n’est dupe.
                        <br />
                        <br />
                        <b>Tout y était.</b>
                    </Typography>

                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faLightbulb} style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="La surprise"
                                secondary={
                                    "Des élus de l’opposition ne s’attendaient pas à délibérer sur la poursuite de l’ensemble du projet sportif (hormis le tennis)."
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#ffd600" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="L’inquiétude"
                                secondary={
                                    "Un vote sur un projet global d’un montant hors norme pour notre commune et l’état de ses finances."
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#2e7d32" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="L’inédit"
                                secondary={
                                    "Des élus de la majorité s’expriment en CM. Trois adjoints de la garde rapprochée du Maire font part de leurs réserves sur leur propre projet."
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faInfoCircle} style={{ color: "#0288d1" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="La tromperie"
                                secondary={
                                    "Un Maire qui a fait miroiter aux associations sportives un projet démesuré, qu’il savait financièrement intenable."
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#d32f2f" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="La cruauté"
                                secondary={
                                    "Un élu délégué aux sports de bonne volonté, mais dépassé par la manipulation et sonné par le manque de solidarité de ses colistiers."
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faGavel} style={{ color: "#9c27b0" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Le dénouement"
                                secondary={
                                    "Retour à la réalité. Pour ne pas trop perdre la face, le Maire a voté pour, mais avait fait le nécessaire pour que la délibération ne soit pas adoptée."
                                }
                            />
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body1">
                        Pourquoi ne pas avoir choisi de lancer ce projet structurant pour la commune, étape par étape, dès 2018, début de sa première mandature ? En travaillant par ordre de priorité, selon les possibilités raisonnables de financement, à commencer par une grande salle polyvalente comprenant un gymnase avec gradins ?
                    </Typography>
                    <Typography variant="body1">
                        Mais non, le projet a été augmenté d’un bloc DOJO, d’une salle multi-activités et d’un terrain synthétique aux Foucherolles... pour un projet estimé à environ 11 millions d’euros HT — sachant que le montant réel est toujours largement supérieur à l’estimation initiale.
                    </Typography>
                    <Typography variant="body1">
                        Les associations sportives n’en demandaient pas tant.<br />
                        Le Maire a donc fait ce qu’il fallait pour saboter son propre projet.
                    </Typography>
                    <Typography variant="body1">
                        Dans notre lettre d’informations n°26 du 23/09/2023, nous avions déjà exprimé nos doutes quant à la volonté du Maire d’investir dans des équipements sportifs (gestion non maîtrisée du projet, lancement tardif, délibérations sans plan de financement ni planning). Tout était déjà écrit.
                    </Typography>

                    <Typography variant="body1" color="error" fontWeight={600}>
                        Après les fortes hausses de la taxe foncière, expliquer aux Bacots que ce projet leur coûterait plus de 600 000 € HT par an sur 30 ans est intenable dans un tel contexte.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Même la stratégie du chantage aux urnes – « si vous voulez que ce magnifique projet sportif se réalise, votez pour moi en 2026 » – n’est plus crédible.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="body1" fontWeight={600} color="secondary" mb={1}>
                        Si la majorité a mis à l’ordre du jour une délibération pour voter contre, c’est qu’elle ne pouvait pas faire autrement pour arrêter officiellement la mascarade d’un projet que chacun savait pourtant mort-né, faute de financement envisageable.
                    </Typography>
                    <Typography variant="body1">
                        Ce vote contre l’ensemble du projet au CM était pour le Maire sa seule porte de sortie possible. Sauf qu’il aura dilapidé environ 200 000 € HT en frais de cabinets de conseils, d’accompagnement d’un avocat, et de concours d’architecte… pour rien !
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ bgcolor: "#e9f5ee", p: 3, borderRadius: 3, boxShadow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <FontAwesomeIcon icon={faEuroSign} style={{ color: "#388e3c" }} />
                            <Typography variant="h6" fontWeight={700} color="success.main">
                                Coût pour les Bacots
                            </Typography>
                        </Stack>
                        <List>
                            <ListItem disablePadding>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faFutbol} style={{ color: "#0288d1" }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Cabinet de conseil ISC"
                                    secondary="20 000 € sur 2022 (état des lieux + propositions) + 30 825 € sur 2023 (programme pour l’architecte)"
                                />
                                <Chip label="50 825 € HT" variant="outlined" />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faGavel} style={{ color: "#ff9800" }} />
                                </ListItemIcon>
                                <ListItemText primary="Accompagnement d’un avocat" />
                                <Chip label="16 000 € HT" variant="outlined" />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#9c27b0" }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Concours d’architecte"
                                    secondary="Les 3 candidats au concours de Maîtrise d’œuvre : 31 000 € chacun pour le second tour, soit 93 000 € HT + 500 € HT par membre du jury par session"
                                />
                                <Chip label="93 000 € HT +" variant="outlined" />
                            </ListItem>
                        </List>
                        <Typography variant="caption" color="text.secondary">
                            * Toutes les sommes indiquées sont hors taxes (HT)
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            mt: 5,
                            mb: 5,
                            bgcolor: "#f7f3ee",
                            borderRadius: 3,
                            p: 4,
                            boxShadow: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "#d32f2f" }} />
                            <Typography variant="h6" fontWeight={700} color="primary">
                                Télécharger le tract au format PDF
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mb={2} align="center">
                            Retrouvez toutes les informations de l'article sous forme de tract à imprimer ou partager !
                        </Typography>
                        <a
                            href="/files/TRACT-42-2025-04-11.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                textDecoration: "none",
                            }}
                        >
                            <Chip
                                icon={<FontAwesomeIcon icon={faDownload} style={{ color: "#fff" }} />}
                                label="Télécharger le PDF"
                                sx={{
                                    bgcolor: "#d32f2f",
                                    color: "#fff",
                                    px: 3,
                                    py: 2,
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    "&:hover": {
                                        bgcolor: "#b71c1c",
                                    },
                                }}
                                clickable
                            />
                        </a>
                    </Box>

                </Box>
            </Container>
        </>
    );
};

export default POST20250411;